/**
 * Webhook Management Route (September 2025)
 *
 * Comprehensive webhook management interface for event notifications.
 * Users can create, edit, delete, and monitor webhook endpoints.
 *
 * Features:
 * - Table layout with responsive Tailwind design
 * - Create webhook modal with TanStack Form validation
 * - Edit webhook configuration
 * - Delete webhook with confirmation
 * - Rotate webhook secrets
 * - View delivery statistics
 * - Enable/disable webhooks
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConvex } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { CreateWebhookModal } from '../../../components/CreateWebhookModal';
import { EditWebhookModal } from '../../../components/EditWebhookModal';
import { WebhookDetailsModal } from '../../../components/WebhookDetailsModal';
import { DeleteWebhookModal } from '../../../components/DeleteWebhookModal';
import { RotateSecretModal } from '../../../components/RotateSecretModal';

export const Route = createFileRoute('/app/dashboard/webhooks')({
  component: WebhooksPage,
});

// Webhook types from backend
type WebhookEventType =
  | 'service_key.rotated'
  | 'service_key.created'
  | 'child_key.created'
  | 'child_key.revoked'
  | 'credits.low_threshold'
  | 'credits.exhausted';

interface Webhook {
  id: Id<'webhooks'>;
  name: string;
  url: string;
  status: 'enabled' | 'disabled';
  events: WebhookEventType[];
  retryConfig: {
    maxRetries: number;
    backoffMultiplier: number;
    initialDelayMs: number;
  };
  customHeaders?: Record<string, string>;
  description?: string;
  lastDeliveryAt?: number;
  lastDeliveryStatus?: 'success' | 'failure';
  lastErrorMessage?: string;
  successCount: number;
  failureCount: number;
  createdAt: number;
  updatedAt: number;
}

function WebhooksPage() {
  const convex = useConvex();
  const queryClient = useQueryClient();

  // State for modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [viewingWebhook, setViewingWebhook] = useState<Webhook | null>(null);
  const [deletingWebhook, setDeletingWebhook] = useState<Webhook | null>(null);
  const [rotatingWebhook, setRotatingWebhook] = useState<Webhook | null>(null);

  // Query webhooks
  const { data: webhooks = [], isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => {
      return await convex.query(api.webhooks.queries.listWebhooks, {});
    },
  });

  return (
    <div className="min-h-screen bg-terminal-black p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-terminal-orange mb-2 terminal-glow">
          [WEBHOOK MANAGEMENT]
        </h1>
        <p className="text-terminal-cyan text-sm">
          Configure event notification endpoints for real-time updates
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="terminal-box p-4">
          <div className="text-terminal-cyan text-sm mb-1">TOTAL WEBHOOKS</div>
          <div className="text-3xl font-bold text-terminal-orange terminal-glow">
            {webhooks.length}
          </div>
        </div>
        <div className="terminal-box p-4">
          <div className="text-terminal-cyan text-sm mb-1">ENABLED</div>
          <div className="text-3xl font-bold text-green-400">
            {webhooks.filter((w) => w.status === 'enabled').length}
          </div>
        </div>
        <div className="terminal-box p-4">
          <div className="text-terminal-cyan text-sm mb-1">TOTAL DELIVERIES</div>
          <div className="text-3xl font-bold text-terminal-orange terminal-glow">
            {webhooks.reduce((sum, w) => sum + w.successCount + w.failureCount, 0)}
          </div>
        </div>
      </div>

      {/* Create Webhook Button */}
      <div className="mb-6">
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow"
        >
          + CREATE WEBHOOK
        </button>
      </div>

      {/* Webhooks Table */}
      {isLoading ? (
        <div className="terminal-box p-8 text-center">
          <div className="text-terminal-cyan text-lg animate-pulse">
            [LOADING WEBHOOKS...]
          </div>
        </div>
      ) : webhooks.length === 0 ? (
        <div className="terminal-box p-12 text-center">
          <div className="text-terminal-cyan text-xl mb-4">
            [NO WEBHOOKS CONFIGURED]
          </div>
          <p className="text-terminal-cyan/70 mb-6 max-w-2xl mx-auto">
            Create your first webhook to receive real-time event notifications.
            Webhooks allow your application to react to API key events, credit changes,
            and other important system events.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow"
          >
            CREATE YOUR FIRST WEBHOOK
          </button>
        </div>
      ) : (
        <div className="terminal-box overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-terminal-cyan">
                <th className="text-left text-terminal-cyan p-4 font-mono">NAME</th>
                <th className="text-left text-terminal-cyan p-4 font-mono">URL</th>
                <th className="text-left text-terminal-cyan p-4 font-mono">STATUS</th>
                <th className="text-left text-terminal-cyan p-4 font-mono">EVENTS</th>
                <th className="text-left text-terminal-cyan p-4 font-mono">SUCCESS RATE</th>
                <th className="text-left text-terminal-cyan p-4 font-mono">LAST DELIVERY</th>
                <th className="text-left text-terminal-cyan p-4 font-mono">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((webhook) => (
                <WebhookRow
                  key={webhook.id}
                  webhook={webhook}
                  onEdit={() => setEditingWebhook(webhook)}
                  onDelete={() => setDeletingWebhook(webhook)}
                  onView={() => setViewingWebhook(webhook)}
                  onRotateSecret={() => setRotatingWebhook(webhook)}
                  onToggleStatus={async () => {
                    const newStatus = webhook.status === 'enabled' ? 'disabled' : 'enabled';
                    await convex.mutation(api.webhooks.mutations.updateWebhook, {
                      webhookId: webhook.id,
                      updates: {
                        status: newStatus,
                      },
                    });
                    queryClient.invalidateQueries({ queryKey: ['webhooks'] });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Webhook Modal */}
      <CreateWebhookModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(webhookId) => {
          console.log('Webhook created:', webhookId);
        }}
      />

      {/* Edit Webhook Modal */}
      <EditWebhookModal
        isOpen={!!editingWebhook}
        webhook={editingWebhook}
        onClose={() => setEditingWebhook(null)}
        onSuccess={() => {
          console.log('Webhook updated');
        }}
      />

      {/* Webhook Details Modal */}
      <WebhookDetailsModal
        isOpen={!!viewingWebhook}
        webhook={viewingWebhook}
        onClose={() => setViewingWebhook(null)}
        onEdit={() => {
          setEditingWebhook(viewingWebhook);
          setViewingWebhook(null);
        }}
        onRotateSecret={() => {
          setRotatingWebhook(viewingWebhook);
          setViewingWebhook(null);
        }}
      />

      {/* Delete Webhook Modal */}
      <DeleteWebhookModal
        isOpen={!!deletingWebhook}
        webhook={deletingWebhook}
        onClose={() => setDeletingWebhook(null)}
        onSuccess={() => {
          console.log('Webhook deleted');
        }}
      />

      {/* Rotate Secret Modal */}
      <RotateSecretModal
        isOpen={!!rotatingWebhook}
        webhook={rotatingWebhook}
        onClose={() => setRotatingWebhook(null)}
        onSuccess={() => {
          console.log('Secret rotated');
        }}
      />
    </div>
  );
}

interface WebhookRowProps {
  webhook: Webhook;
  onEdit: () => void;
  onDelete: () => void;
  onView: () => void;
  onRotateSecret: () => void;
  onToggleStatus: () => Promise<void>;
}

function WebhookRow({ webhook, onEdit, onDelete, onView, onToggleStatus }: WebhookRowProps) {
  const totalDeliveries = webhook.successCount + webhook.failureCount;
  const successRate = totalDeliveries > 0
    ? Math.round((webhook.successCount / totalDeliveries) * 100)
    : 0;

  const formatRelativeTime = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <tr className="border-b border-terminal-cyan/30 hover:bg-terminal-cyan/5 transition-colors">
      <td className="p-4 text-terminal-orange font-semibold">{webhook.name}</td>
      <td className="p-4 text-terminal-cyan text-sm font-mono truncate max-w-xs">
        {webhook.url}
      </td>
      <td className="p-4">
        <span
          className={`inline-block px-3 py-1 text-xs font-bold border-2 ${
            webhook.status === 'enabled'
              ? 'text-green-400 border-green-400'
              : 'text-red-400 border-red-400'
          }`}
        >
          [{webhook.status.toUpperCase()}]
        </span>
      </td>
      <td className="p-4 text-terminal-cyan text-sm">
        {webhook.events.length} event{webhook.events.length !== 1 ? 's' : ''}
      </td>
      <td className="p-4">
        <div className="text-terminal-cyan text-sm">
          {successRate}% ({webhook.successCount}/{totalDeliveries})
        </div>
      </td>
      <td className="p-4 text-terminal-cyan text-sm">
        {webhook.lastDeliveryAt ? (
          <div>
            <div>{formatRelativeTime(webhook.lastDeliveryAt)}</div>
            <div
              className={`text-xs ${
                webhook.lastDeliveryStatus === 'success'
                  ? 'text-green-400'
                  : 'text-red-400'
              }`}
            >
              {webhook.lastDeliveryStatus?.toUpperCase() || 'N/A'}
            </div>
          </div>
        ) : (
          'Never'
        )}
      </td>
      <td className="p-4">
        <div className="flex gap-2">
          <button
            onClick={onView}
            className="text-terminal-cyan hover:text-terminal-orange text-xs border border-terminal-cyan hover:border-terminal-orange px-2 py-1 transition-colors"
            title="View Details"
          >
            VIEW
          </button>
          <button
            onClick={onEdit}
            className="text-terminal-cyan hover:text-terminal-orange text-xs border border-terminal-cyan hover:border-terminal-orange px-2 py-1 transition-colors"
            title="Edit Webhook"
          >
            EDIT
          </button>
          <button
            onClick={onToggleStatus}
            className="text-terminal-cyan hover:text-terminal-orange text-xs border border-terminal-cyan hover:border-terminal-orange px-2 py-1 transition-colors"
            title={webhook.status === 'enabled' ? 'Disable' : 'Enable'}
          >
            {webhook.status === 'enabled' ? 'DISABLE' : 'ENABLE'}
          </button>
          <button
            onClick={onDelete}
            className="text-red-400 hover:text-red-300 text-xs border border-red-400 hover:border-red-300 px-2 py-1 transition-colors"
            title="Delete Webhook"
          >
            DELETE
          </button>
        </div>
      </td>
    </tr>
  );
}
